import { useEffect, useRef } from "preact/hooks";

const focusable =
  'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useModalFocus<T extends HTMLElement>(
  initialSelector = "button, [tabindex='-1']",
) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const previous = document.activeElement as HTMLElement | null;
    const siblings = [...(dialog.parentElement?.children ?? [])].filter(
      (node) => node !== dialog,
    ) as HTMLElement[];
    const inertBefore = siblings.map((node) => node.inert);
    siblings.forEach((node) => {
      node.inert = true;
    });
    const first = dialog.querySelector<HTMLElement>(initialSelector) ?? dialog;
    if (first === dialog) dialog.setAttribute("tabindex", "-1");
    first.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const items = [...dialog.querySelectorAll<HTMLElement>(focusable)].filter(
        (item) => item.offsetParent !== null,
      );
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const current = document.activeElement as HTMLElement;
      const index = items.indexOf(current);
      const next = event.shiftKey
        ? index <= 0
          ? items.at(-1)
          : items[index - 1]
        : index === items.length - 1
          ? items[0]
          : items[index + 1];
      if (index === -1 || next) {
        event.preventDefault();
        next?.focus();
      }
    };
    dialog.addEventListener("keydown", onKeyDown);
    return () => {
      dialog.removeEventListener("keydown", onKeyDown);
      siblings.forEach((node, index) => {
        node.inert = inertBefore[index];
      });
      if (previous?.isConnected) previous.focus();
    };
  }, [initialSelector]);
  return ref;
}
